import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
 
// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
    primary:            '#6a37d4',
    primaryLight:       '#ae8dff',
    primaryContainer:   '#ede8ff',
    secondary:          '#006947',
    secondaryContainer: '#d1fae5',
    tertiary:           '#6f5900',
    tertiaryContainer:  '#fed01b',
    surface:            '#f3f6ff',
    surfaceLow:         '#eaf1ff',
    surfaceHigh:        '#d6e3f9',
    surfaceLowest:      '#ffffff',
    onBackground:       '#252f3c',
    onSurface:          '#252f3c',
    onSurfaceVariant:   '#525c6a',
    outlineVariant:     '#a4aebe',
};
 
interface Resource {
    id: string;
    emoji: string;
    title: string;
    subtitle: string;
    description: string;
    includes: string[];
    filename: string;
    accentBg: string;
    accentColor: string;
}
 
const resources: Resource[] = [
    {
        id: 'prompt-cards',
        emoji: '🃏',
        title: 'Story Prompt Cards',
        subtitle: '24 story sparks · Print & cut',
        description: 'Cut them out, keep them in a jar, and pull one out whenever you\'re stuck. Each card has a story-starting scenario designed to fire up the imagination.',
        includes: [
            '24 unique story prompts',
            'Print & cut card format',
            'Fantasy, mystery, adventure & real-life themes',
            'Ages 9–13',
        ],
        filename: 'Prompts_Free.pdf',
        accentBg: C.primaryContainer,
        accentColor: C.primary,
    },
    {
        id: 'story-builder',
        emoji: '📋',
        title: 'Creative Story Builder Pack',
        subtitle: '3 worksheets · Plan before you write',
        description: 'Three printable worksheets that help young writers plan their story from start to finish — before a single word hits the page.',
        includes: [
            'Story Development worksheet',
            'Character Development worksheet',
            'Story Setting worksheet',
            'Ages 9–13',
        ],
        filename: 'Creative_Story_Builder_Pack_Free.pdf',
        accentBg: C.secondaryContainer,
        accentColor: C.secondary,
    },
];
 
const FreeResources: React.FC = () => {
    const navigate = useNavigate();
    const [hoveredId, setHoveredId] = useState<string | null>(null);
    const [isMobile] = useState(window.innerWidth < 768);

    const handleDownload = async (filename: string, title: string) => {
        try {
            await addDoc(collection(db, 'resourceDownloads'), {
                resource: filename,
                title,
                downloadedAt: serverTimestamp(),
            });
        } catch (e) {
            console.error('Failed to log download:', e);
        }
        const link = document.createElement('a');
        link.href = `/resources/${filename}`;
        link.download = filename;
        link.click();
    };
 
    return (
        <div style={{
            minHeight: '100vh',
            background: C.surface,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            paddingBottom: 80,
        }}>
 
            {/* ── Hero ──────────────────────────────────────────────────── */}
            <div style={{
                background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)`,
                padding: isMobile ? '48px 24px 56px' : '72px 48px 80px',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
            }}>
                {/* Decorative circles */}
                {[
                    { size: 300, top: -80, right: -60, opacity: 0.08 },
                    { size: 200, bottom: -60, left: -40, opacity: 0.06 },
                    { size: 120, top: 20, left: '30%', opacity: 0.05 },
                ].map((c, i) => (
                    <div key={i} style={{
                        position: 'absolute',
                        width: c.size, height: c.size,
                        borderRadius: '50%',
                        background: '#fff',
                        opacity: c.opacity,
                        top: c.top, bottom: c.bottom,
                        left: c.left, right: c.right,
                        pointerEvents: 'none',
                    }} />
                ))}
 
                <div style={{ position: 'relative', zIndex: 1, maxWidth: 640, margin: '0 auto' }}>
                    <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8,
                        background: 'rgba(255,255,255,0.15)',
                        border: '1px solid rgba(255,255,255,0.25)',
                        borderRadius: 9999, padding: '6px 16px',
                        fontSize: '0.75rem', fontWeight: 700,
                        color: '#fff', letterSpacing: '1.5px',
                        textTransform: 'uppercase',
                        marginBottom: 20,
                        fontFamily: "'Epilogue', sans-serif",
                    }}>
                        ✨ Free to download · No sign-up needed
                    </div>
 
                    <h1 style={{
                        fontFamily: "'Epilogue', sans-serif",
                        fontSize: isMobile ? '2.2rem' : '3rem',
                        fontWeight: 900,
                        color: '#fff',
                        margin: '0 0 16px',
                        lineHeight: 1.15,
                        letterSpacing: -0.5,
                    }}>
                        Free Resources for<br />Young Writers
                    </h1>
 
                    <p style={{
                        fontSize: isMobile ? '1rem' : '1.1rem',
                        color: 'rgba(255,255,255,0.85)',
                        margin: '0 auto',
                        lineHeight: 1.7,
                        maxWidth: 480,
                    }}>
                        Print them, use them in class, share them at home.
                        Everything here is free to use with young writers ages 9–13.
                    </p>
                </div>
            </div>
 
            {/* ── Resource cards ────────────────────────────────────────── */}
            <div style={{
                maxWidth: 900,
                margin: '0 auto',
                padding: isMobile ? '40px 16px' : '64px 48px',
                display: 'flex',
                flexDirection: 'column',
                gap: 28,
            }}>
 
                {resources.map(r => {
                    const hovered = hoveredId === r.id;
                    return (
                        <div
                            key={r.id}
                            onMouseEnter={() => setHoveredId(r.id)}
                            onMouseLeave={() => setHoveredId(null)}
                            style={{
                                background: C.surfaceLowest,
                                borderRadius: 24,
                                border: `1.5px solid ${hovered ? r.accentColor + '40' : C.surfaceHigh}`,
                                boxShadow: hovered
                                    ? `0 16px 48px rgba(0,0,0,0.1), 0 0 0 3px ${r.accentColor}18`
                                    : '0 2px 12px rgba(0,0,0,0.05)',
                                transition: 'all 0.25s',
                                transform: hovered ? 'translateY(-3px)' : 'translateY(0)',
                                overflow: 'hidden',
                            }}
                        >
                            <div style={{
                                display: 'flex',
                                flexDirection: isMobile ? 'column' : 'row',
                                gap: 0,
                            }}>
                                {/* Left accent panel */}
                                <div style={{
                                    background: r.accentBg,
                                    padding: isMobile ? '28px 24px' : '40px 36px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: isMobile ? 'flex-start' : 'center',
                                    justifyContent: 'center',
                                    minWidth: isMobile ? 'auto' : 200,
                                    gap: 12,
                                    borderRight: isMobile ? 'none' : `1.5px solid ${r.accentColor}20`,
                                    borderBottom: isMobile ? `1.5px solid ${r.accentColor}20` : 'none',
                                }}>
                                    <div style={{ fontSize: isMobile ? '3rem' : '3.5rem' }}>{r.emoji}</div>
                                    <div style={{
                                        background: r.accentColor,
                                        color: '#fff',
                                        padding: '4px 14px',
                                        borderRadius: 9999,
                                        fontSize: '0.65rem',
                                        fontWeight: 800,
                                        fontFamily: "'Epilogue', sans-serif",
                                        textTransform: 'uppercase',
                                        letterSpacing: '1px',
                                        whiteSpace: 'nowrap',
                                    }}>
                                        Free PDF
                                    </div>
                                </div>
 
                                {/* Main content */}
                                <div style={{
                                    flex: 1,
                                    padding: isMobile ? '24px' : '36px 40px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 16,
                                }}>
                                    <div>
                                        <div style={{
                                            fontSize: '0.72rem',
                                            fontWeight: 700,
                                            color: r.accentColor,
                                            fontFamily: "'Epilogue', sans-serif",
                                            textTransform: 'uppercase',
                                            letterSpacing: '1px',
                                            marginBottom: 6,
                                        }}>
                                            {r.subtitle}
                                        </div>
                                        <h2 style={{
                                            fontFamily: "'Epilogue', sans-serif",
                                            fontWeight: 900,
                                            fontSize: isMobile ? '1.3rem' : '1.6rem',
                                            color: C.onBackground,
                                            margin: '0 0 10px',
                                            lineHeight: 1.2,
                                        }}>
                                            {r.title}
                                        </h2>
                                        <p style={{
                                            fontSize: '0.9rem',
                                            color: C.onSurfaceVariant,
                                            margin: 0,
                                            lineHeight: 1.7,
                                        }}>
                                            {r.description}
                                        </p>
                                    </div>
 
                                    {/* What's inside */}
                                    <div style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 8,
                                    }}>
                                        {r.includes.map(item => (
                                            <div key={item} style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 6,
                                                background: r.accentBg,
                                                padding: '5px 12px',
                                                borderRadius: 9999,
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                color: r.accentColor,
                                            }}>
                                                <span style={{ fontSize: '0.6rem' }}>✓</span>
                                                {item}
                                            </div>
                                        ))}
                                    </div>
 
                                    {/* Download button */}
                                    <div style={{ marginTop: 4 }}>
                                    <button
    onClick={() => handleDownload(r.filename, r.title)}
    style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '12px 28px',
        background: `linear-gradient(135deg, ${r.accentColor}, ${r.accentColor}cc)`,
        color: '#fff',
        borderRadius: 9999,
        fontWeight: 700,
        fontSize: '0.9rem',
        fontFamily: "'Epilogue', sans-serif",
        border: 'none',
        cursor: 'pointer',
        boxShadow: `0 4px 14px ${r.accentColor}40`,
        transition: 'opacity 0.15s, transform 0.15s',
    }}
    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.opacity = '0.9'; (e.currentTarget as HTMLElement).style.transform = 'scale(1.02)'; }}
    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.opacity = '1'; (e.currentTarget as HTMLElement).style.transform = 'scale(1)'; }}
>
    ⬇ Download Free PDF
</button>
                                    
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
 
            {/* ── CTA banner ────────────────────────────────────────────── */}
            <div style={{
                maxWidth: 900,
                margin: '0 auto',
                padding: isMobile ? '0 16px' : '0 48px',
            }}>
                <div style={{
                    background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)`,
                    borderRadius: 24,
                    padding: isMobile ? '36px 28px' : '48px 56px',
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    justifyContent: 'space-between',
                    gap: 24,
                }}>
                    <div>
                        <h3 style={{
                            fontFamily: "'Epilogue', sans-serif",
                            fontWeight: 900,
                            fontSize: isMobile ? '1.3rem' : '1.6rem',
                            color: '#fff',
                            margin: '0 0 8px',
                            lineHeight: 1.25,
                        }}>
                            Ready to write the story?
                        </h3>
                        <p style={{
                            fontSize: '0.9rem',
                            color: 'rgba(255,255,255,0.8)',
                            margin: 0,
                            lineHeight: 1.6,
                        }}>
                            Scriblish is a free creative writing platform built for ages 9–13.
                            Write, design your cover, and publish your book.
                        </p>
                    </div>
                    <button
                        onClick={() => navigate('/signup')}
                        style={{
                            background: '#fff',
                            color: C.primary,
                            border: 'none',
                            borderRadius: 9999,
                            padding: '13px 32px',
                            fontWeight: 800,
                            fontSize: '0.95rem',
                            fontFamily: "'Epilogue', sans-serif",
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                            transition: 'transform 0.15s',
                        }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(1.04)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(1)'; }}
                    >
                        Start Writing Free →
                    </button>
                </div>
            </div>
 
            {/* ── Footer note ───────────────────────────────────────────── */}
            <p style={{
                textAlign: 'center',
                fontSize: '0.78rem',
                color: C.outlineVariant,
                marginTop: 48,
                padding: '0 24px',
                lineHeight: 1.6,
            }}>
                © Scriblish · Free to share with young writers, teachers, and librarians
            </p>
        </div>
    );
};
 
export default FreeResources;